// deps
import React, { memo, ReactElement, useEffect, useState, useMemo, ReactNode } from "react";

// internal deps
import { isEmpty } from "@swiggy-private/js-utils";

// constants
import { TICK_STATES, DEFAULT_ACCESSIBILITY_TEXTS, SUB_TEXT_TIMEOUT } from "../constants";
import { TickScreenProps } from "../interfaces";

// components
import TickScreenUi from "./TickScreenUi";

function TickScreen(props: TickScreenProps): ReactElement {
    const {
        postSuccessCb,
        isSuper = false,
        customSuccessScreen = false,
        isSwiggyMoney = false,
        showBottomAnimation = false,
        subTextNodeList,
        tagElement,
        isJioHotstarFlow = false,
    } = props;
    const [tickState, setTickState] = useState<TICK_STATES>(props.tickState);
    const [mainText, setMainText] = useState(props.mainText);
    const [subText, setSubText] = useState(props.subText);
    const [intermediateText, setIntermediateText] = useState(props.intermediateText);
    const [subtextCounter, setSubtextCounter] = useState(0);
    const [subTextNode, setSubTextNode] = useState<ReactNode>(null);
    const successTimeout = !isEmpty(subTextNodeList) ? subTextNodeList.length * SUB_TEXT_TIMEOUT : SUB_TEXT_TIMEOUT;

    useEffect(() => {
        if (props.tickState !== TICK_STATES.SUCCESS) {
            return;
        }
        // delay confirm tick
        const timeOut = window.setTimeout(() => {
            setTickState(props.tickState);
            setMainText(props.mainText);
            setSubText(props.subText);
            setIntermediateText(props.intermediateText);

            if (!isEmpty(subTextNodeList)) {
                setSubTextNode(subTextNodeList[subtextCounter]);
            }
        }, 1000);

        return (): void => clearTimeout(timeOut);
    }, [
        props.mainText,
        props.subText,
        props.intermediateText,
        props.tickState,
        tickState,
        subTextNodeList,
        subtextCounter,
    ]);

    useEffect(() => {
        if (props.tickState !== TICK_STATES.SUCCESS || isEmpty(subTextNodeList)) {
            return;
        }

        const interval = window.setInterval(() => {
            if (subtextCounter === subTextNodeList.length - 1) {
                clearInterval(interval);
                return;
            }
            setSubtextCounter(subtextCounter + 1);
        }, SUB_TEXT_TIMEOUT);

        return (): void => clearInterval(interval);
    }, [props.tickState, subtextCounter, subTextNodeList]);

    const a11yStatus = useMemo(() => {
        const a11Texts = props.a11yTexts != null ? props.a11yTexts : DEFAULT_ACCESSIBILITY_TEXTS;
        return tickState == TICK_STATES.SUCCESS ? a11Texts[TICK_STATES.SUCCESS] : a11Texts[TICK_STATES.LOADING];
    }, [props.a11yTexts, tickState]);

    return (
        <TickScreenUi
            tickState={tickState}
            mainText={mainText}
            subText={subText}
            subTextNode={subTextNode}
            intermediateText={intermediateText}
            tagElement={tagElement}
            postSuccessCb={postSuccessCb}
            a11yStatus={a11yStatus}
            customSuccessScreen={customSuccessScreen}
            tickAccent={props.accent}
            showBottomAnimation={showBottomAnimation}
            successTimeout={successTimeout}
            isSuper={isSuper}
            isSwiggyMoney={isSwiggyMoney}
            isJioHotstarFlow={isJioHotstarFlow}
        />
    );
}

export default memo(TickScreen);
